// Regions: the risk/reward dial of the world map (DESIGN §12).
// risk keyframes are interpolated linearly by year and clamped at the ends. All values 0-1:
//   stability     1 = calm, 0 = coups/revolution (drives regional news)
//   security      1 = perfectly safe, 0 = war zone (drives attack chance)
//   expropriation annual chance the government seizes your assets (M2+)
//   corruption    how much bribes help (and how often they are needed)
// leaseMult scales field lease prices; transportToMarket = extra $/bbl (base-year $, CPI-scaled) to reach world buyers.
// lat/lon are for the map pin. `opensYear` is when foreigners can first operate there.

import { M2_REGIONS, M2_RISK_KEYFRAMES, mergeRiskKeyframes } from './m2/regions.js';
import { M3_REGIONS, M3_RISK_KEYFRAMES, mergeM3RiskKeyframes } from './m3/regions.js';
import { M4_REGIONS, M4_RISK_KEYFRAMES, mergeM4RiskKeyframes } from './m4/regions.js';
import { m4Obj } from './m4/gate.js';

const r = (year, stability, security, expropriation, corruption) => ({ year, stability, security, expropriation, corruption });

export const REGIONS = {
  // ------------------------------------------------------------------ Pioneer-era (M1)
  pennsylvania: {
    id: 'pennsylvania', name: 'Pennsylvania, USA', country: 'United States', continent: 'north_america', currency: 'USD',
    lat: 41.6, lon: -79.7, opensYear: 1859, leaseMult: 1.0, royalty: 0.125, transportToMarket: 0,
    hazards: ['theft'],
    risk: [r(1859, 0.95, 0.92, 0, 0.2), r(1861, 0.85, 0.9, 0, 0.25), r(1866, 0.9, 0.85, 0, 0.3), // Pithole lawlessness
           r(1872, 0.85, 0.9, 0, 0.35), r(1900, 0.95, 0.97, 0, 0.2), r(2025, 0.97, 0.99, 0, 0.1)],
    blurb: 'The Oil Region. Courts, sheriffs and railroads; the only bandits wear Standard Oil suits.',
  },
  ohio: {
    id: 'ohio', name: 'Ohio / Indiana, USA', country: 'United States', continent: 'north_america', currency: 'USD',
    lat: 40.74, lon: -84.1, opensYear: 1885, leaseMult: 0.8, royalty: 0.125, transportToMarket: 0.1,
    hazards: ['theft'],
    risk: [r(1859, 0.95, 0.95, 0, 0.2), r(2025, 0.97, 0.99, 0, 0.1)],
    blurb: 'Flat farm country over the Trenton limestone. Safe, sour and plentiful.',
  },
  ontario: {
    id: 'ontario', name: 'Canada West (Ontario)', country: 'Canada', continent: 'north_america', currency: 'USD',
    lat: 42.78, lon: -82.12, opensYear: 1858, leaseMult: 0.5, royalty: 0.10, transportToMarket: 0.4,
    hazards: ['theft'],
    risk: [r(1858, 0.92, 0.9, 0, 0.2), r(1866, 0.85, 0.8, 0, 0.2), // Fenian raids
           r(1871, 0.95, 0.95, 0, 0.15), r(2025, 0.98, 0.99, 0, 0.05)],
    blurb: 'Gum beds in the Enniskillen swamp. Safe, shallow and cheap, but the wells are small.',
  },
  romania: {
    id: 'romania', name: 'Wallachia (Romania)', country: 'Romania', continent: 'europe', currency: 'USD',
    lat: 44.94, lon: 26.02, opensYear: 1857, leaseMult: 0.45, royalty: 0.10, transportToMarket: 1.2,
    hazards: ['bandits', 'theft', 'sabotage'],
    risk: [r(1857, 0.6, 0.55, 0.02, 0.6), r(1876, 0.55, 0.5, 0.02, 0.6), r(1877, 0.3, 0.3, 0.04, 0.65), // Russo-Turkish War
           r(1879, 0.6, 0.55, 0.02, 0.6), r(1907, 0.35, 0.4, 0.03, 0.6), // peasants' revolt
           r(1916, 0.15, 0.1, 0.1, 0.6), // German advance; British sabotage of the wells
           r(1920, 0.5, 0.5, 0.05, 0.6), r(1944, 0.1, 0.05, 0.2, 0.6), r(1948, 0.3, 0.6, 0.95, 0.7), // Communist nationalization
           r(1990, 0.5, 0.7, 0.1, 0.6), r(2025, 0.85, 0.9, 0.01, 0.35)],
    blurb: 'Hand-dug pits around Ploiești turning into proper wells. Cheap leases, a border full of armies.',
  },
  galicia: {
    id: 'galicia', name: 'Galicia, Austria-Hungary', country: 'Austria-Hungary', continent: 'europe', currency: 'USD',
    lat: 49.29, lon: 23.42, opensYear: 1870, leaseMult: 0.5, royalty: 0.10, transportToMarket: 1.0,
    hazards: ['strike', 'theft', 'sabotage'],
    risk: [r(1870, 0.7, 0.65, 0.02, 0.5), r(1904, 0.6, 0.55, 0.02, 0.5), // Boryslav strikes
           r(1914, 0.2, 0.15, 0.05, 0.5), r(1919, 0.4, 0.4, 0.1, 0.5), r(1939, 0.1, 0.1, 0.9, 0.5), // Soviet seizure
           r(1991, 0.5, 0.6, 0.2, 0.7), r(2014, 0.4, 0.45, 0.1, 0.65), r(2025, 0.3, 0.35, 0.08, 0.6)],
    blurb: 'Boryslav: ozokerite wax pits, wildcat shafts and a restless labor force.',
  },
  baku: {
    id: 'baku', name: 'Baku, Russian Empire', country: 'Russian Empire', continent: 'asia', currency: 'USD',
    lat: 40.45, lon: 49.95, opensYear: 1872, leaseMult: 0.4, royalty: 0.10, transportToMarket: 1.8,
    hazards: ['bandits', 'sabotage', 'uprising', 'theft'],
    risk: [r(1859, 0.6, 0.5, 0.05, 0.5), r(1872, 0.6, 0.45, 0.04, 0.55), r(1890, 0.55, 0.45, 0.04, 0.55),
           r(1903, 0.35, 0.3, 0.06, 0.6), r(1905, 0.2, 0.15, 0.1, 0.6), // 1905 revolution, Armenian-Tatar clashes
           r(1910, 0.4, 0.35, 0.08, 0.55), r(1918, 0.1, 0.1, 0.5, 0.5),
           r(1920, 0.1, 0.2, 0.95, 0.5), // Bolshevik nationalization
           r(1991, 0.3, 0.4, 0.2, 0.7), r(2025, 0.6, 0.65, 0.05, 0.6)],
    blurb: 'Balakhany: gushers that blow for months and bandits who ride in from the hills. Leases sold at auction from 1872.',
  },
  sumatra: {
    id: 'sumatra', name: 'Sumatra, Dutch East Indies', country: 'Dutch East Indies', continent: 'asia', currency: 'USD',
    lat: 3.95, lon: 98.2, opensYear: 1885, leaseMult: 0.3, royalty: 0.08, transportToMarket: 2.2,
    hazards: ['bandits', 'disease', 'uprising', 'sabotage'],
    risk: [r(1873, 0.35, 0.3, 0.03, 0.5), // Aceh War begins
           r(1885, 0.35, 0.3, 0.03, 0.5), r(1904, 0.5, 0.45, 0.03, 0.5), // Aceh mostly subdued
           r(1942, 0.1, 0.05, 0.8, 0.5), // Japanese invasion, scorched earth
           r(1949, 0.3, 0.3, 0.4, 0.7), r(1958, 0.25, 0.3, 0.6, 0.7), // Indonesian nationalizations
           r(2000, 0.4, 0.4, 0.1, 0.7), r(2025, 0.65, 0.7, 0.05, 0.55)],
    blurb: 'Telaga Said in the jungle of Langkat. Fever, tigers and Acehnese raiders guard very rich oil.',
  },

  // ------------------------------------------------------------------ later eras (metadata; M2+)
  texas: { id: 'texas', name: 'Texas, USA', country: 'United States', continent: 'north_america', currency: 'USD',
    lat: 30.0, lon: -94.1, opensYear: 1901, leaseMult: 0.9, royalty: 0.125, transportToMarket: 0.1, hazards: ['theft'],
    risk: [r(1901, 0.85, 0.8, 0, 0.35), r(1931, 0.7, 0.7, 0, 0.45), r(1950, 0.95, 0.95, 0, 0.2), r(2025, 0.97, 0.98, 0, 0.1)],
    blurb: 'Salt domes, boomtowns and the Railroad Commission.' },
  oklahoma: { id: 'oklahoma', name: 'Oklahoma, USA', country: 'United States', continent: 'north_america', currency: 'USD',
    lat: 35.9, lon: -96.2, opensYear: 1905, leaseMult: 0.85, royalty: 0.125, transportToMarket: 0.2, hazards: ['theft'],
    risk: [r(1905, 0.8, 0.75, 0, 0.4), r(1950, 0.95, 0.95, 0, 0.2), r(2025, 0.97, 0.98, 0, 0.1)],
    blurb: 'Glenn Pool and Cushing. Boomtown rough at first.' },
  california: { id: 'california', name: 'California, USA', country: 'United States', continent: 'north_america', currency: 'USD',
    lat: 33.8, lon: -118.17, opensYear: 1900, leaseMult: 1.1, royalty: 0.125, transportToMarket: 0.3, hazards: ['theft'],
    risk: [r(1900, 0.9, 0.85, 0, 0.3), r(2025, 0.97, 0.99, 0, 0.1)],
    blurb: 'Signal Hill forests of derricks above the Pacific.' },
  gulf_of_mexico: { id: 'gulf_of_mexico', name: 'US Gulf of Mexico', country: 'United States', continent: 'north_america', currency: 'USD',
    lat: 28.0, lon: -90.5, opensYear: 1947, leaseMult: 1.0, royalty: 0.1667, transportToMarket: 0.2, hazards: ['storm'],
    risk: [r(1947, 0.95, 0.95, 0, 0.2), r(2025, 0.97, 0.98, 0, 0.1)],
    blurb: 'Out of sight of land. Hurricanes are the only raiders.' },
  alaska: { id: 'alaska', name: 'Alaska North Slope', country: 'United States', continent: 'north_america', currency: 'USD',
    lat: 70.3, lon: -148.5, opensYear: 1968, leaseMult: 0.8, royalty: 0.125, transportToMarket: 2.5, hazards: ['cold'],
    risk: [r(1968, 0.95, 0.97, 0, 0.15), r(2025, 0.97, 0.99, 0, 0.1)],
    blurb: 'Prudhoe Bay: permafrost, darkness and an 800-mile pipeline.' },
  mexico: { id: 'mexico', name: 'Mexico (Golden Lane)', country: 'Mexico', continent: 'north_america', currency: 'USD',
    lat: 21.5, lon: -97.6, opensYear: 1901, leaseMult: 0.4, royalty: 0.10, transportToMarket: 0.8,
    hazards: ['bandits', 'uprising', 'sabotage', 'kidnapping', 'expropriation'],
    risk: [r(1901, 0.7, 0.6, 0.02, 0.6), r(1910, 0.2, 0.15, 0.05, 0.7), // revolution
           r(1920, 0.45, 0.4, 0.1, 0.7), r(1937, 0.4, 0.5, 0.4, 0.7), r(1938, 0.4, 0.5, 0.99, 0.7), // PEMEX expropriation
           r(2000, 0.6, 0.5, 0.1, 0.7), r(2010, 0.5, 0.3, 0.05, 0.75), r(2025, 0.55, 0.35, 0.05, 0.7)],
    blurb: 'Golden Lane wells of legend, revolutionary armies, and 1938.' },
  venezuela: { id: 'venezuela', name: 'Venezuela (Maracaibo)', country: 'Venezuela', continent: 'south_america', currency: 'USD',
    lat: 10.3, lon: -71.4, opensYear: 1914, leaseMult: 0.4, royalty: 0.11, transportToMarket: 0.9,
    hazards: ['bandits', 'sabotage', 'kidnapping', 'expropriation'],
    risk: [r(1914, 0.6, 0.6, 0.02, 0.75), r(1948, 0.5, 0.6, 0.05, 0.7), r(1976, 0.6, 0.65, 0.9, 0.6), // nationalization
           r(1992, 0.4, 0.5, 0.1, 0.7), r(2007, 0.3, 0.35, 0.95, 0.85), r(2025, 0.15, 0.2, 0.5, 0.95)],
    blurb: 'Derricks standing in Lake Maracaibo. Generous until it is not.' },
  persia: { id: 'persia', name: 'Persia / Iran', country: 'Persia', continent: 'asia', currency: 'USD',
    lat: 31.95, lon: 49.3, opensYear: 1901, leaseMult: 0.3, royalty: 0.16, transportToMarket: 1.2,
    hazards: ['bandits', 'sabotage', 'uprising', 'expropriation'],
    risk: [r(1901, 0.5, 0.4, 0.02, 0.7), r(1950, 0.4, 0.5, 0.2, 0.7), r(1951, 0.2, 0.4, 0.99, 0.7), // Mossadegh nationalization
           r(1954, 0.6, 0.6, 0.05, 0.7), r(1978, 0.3, 0.4, 0.3, 0.75), r(1979, 0.05, 0.1, 0.99, 0.8),
           r(2025, 0.3, 0.3, 0.6, 0.8)],
    blurb: 'Masjed Soleyman and the Abadan refinery. A concession that rewrote world politics.' },
  iraq: { id: 'iraq', name: 'Iraq (Kirkuk)', country: 'Iraq', continent: 'asia', currency: 'USD',
    lat: 35.47, lon: 44.39, opensYear: 1927, leaseMult: 0.3, royalty: 0.2, transportToMarket: 1.3,
    hazards: ['sabotage', 'uprising', 'kidnapping', 'expropriation'],
    risk: [r(1927, 0.5, 0.5, 0.05, 0.6), r(1958, 0.3, 0.4, 0.3, 0.7), r(1972, 0.3, 0.4, 0.99, 0.7),
           r(2003, 0.05, 0.05, 0.3, 0.9), r(2010, 0.25, 0.25, 0.1, 0.85), r(2025, 0.4, 0.4, 0.1, 0.8)],
    blurb: 'Baba Gurgur’s eternal fire. Rich beyond measure, rarely at peace.' },
  saudi: { id: 'saudi', name: 'Saudi Arabia', country: 'Saudi Arabia', continent: 'asia', currency: 'USD',
    lat: 25.4, lon: 49.6, opensYear: 1933, leaseMult: 0.25, royalty: 0.12, transportToMarket: 1.0, // royalty 12% until the 1950 fifty-fifty (ROYALTY_SCHEDULE.saudi)
    hazards: ['sabotage', 'expropriation'],
    risk: [r(1933, 0.75, 0.7, 0.02, 0.5), r(1973, 0.8, 0.8, 0.3, 0.5), r(1980, 0.8, 0.8, 0.95, 0.5), // Aramco buy-out
           r(2025, 0.8, 0.75, 0.2, 0.5)],
    blurb: 'Ghawar. Concession terms 50/50 from 1950, and the Kingdom always holds the last card.' },
  kuwait: { id: 'kuwait', name: 'Kuwait (Burgan)', country: 'Kuwait', continent: 'asia', currency: 'USD',
    lat: 28.9, lon: 47.95, opensYear: 1938, leaseMult: 0.25, royalty: 0.12, transportToMarket: 1.0, // 1934 KOC concession ~12%; 50/50 from Dec 1951 (ROYALTY_SCHEDULE.kuwait)
    hazards: ['sabotage', 'expropriation'],
    risk: [r(1938, 0.8, 0.75, 0.02, 0.5), r(1975, 0.8, 0.8, 0.95, 0.5), r(1990, 0.05, 0.02, 0.5, 0.5), r(1992, 0.7, 0.7, 0.3, 0.5), r(2025, 0.8, 0.8, 0.3, 0.5)],
    blurb: 'Burgan: the second-largest field on Earth under a sheet of sand.' },
  libya: { id: 'libya', name: 'Libya (Sirte Basin)', country: 'Libya', continent: 'africa', currency: 'USD',
    lat: 29.0, lon: 19.5, opensYear: 1955, leaseMult: 0.3, royalty: 0.2, transportToMarket: 0.6,
    hazards: ['sabotage', 'uprising', 'kidnapping', 'expropriation'],
    risk: [r(1955, 0.6, 0.65, 0.05, 0.6), r(1969, 0.4, 0.55, 0.3, 0.7), r(1973, 0.5, 0.6, 0.95, 0.7),
           r(2011, 0.05, 0.05, 0.4, 0.9), r(2025, 0.2, 0.15, 0.3, 0.9)],
    blurb: 'Light sweet crude a short tanker hop from Europe.' },
  nigeria: { id: 'nigeria', name: 'Niger Delta, Nigeria', country: 'Nigeria', continent: 'africa', currency: 'USD',
    lat: 4.8, lon: 6.2, opensYear: 1956, leaseMult: 0.3, royalty: 0.2, transportToMarket: 0.7,
    hazards: ['bandits', 'sabotage', 'kidnapping', 'theft', 'uprising'],
    risk: [r(1956, 0.6, 0.6, 0.05, 0.7), r(1967, 0.1, 0.1, 0.2, 0.8), // Biafran war
           r(1971, 0.5, 0.5, 0.3, 0.85), r(1995, 0.35, 0.35, 0.1, 0.9), r(2006, 0.2, 0.1, 0.1, 0.9), // MEND militants
           r(2010, 0.35, 0.3, 0.1, 0.9), r(2025, 0.4, 0.3, 0.1, 0.85)],
    blurb: 'Creeks, mangroves, pipeline taps and militants in speedboats.' },
  north_sea_uk: { id: 'north_sea_uk', name: 'North Sea (UK)', country: 'United Kingdom', continent: 'europe', currency: 'USD',
    lat: 57.7, lon: 1.0, opensYear: 1965, leaseMult: 0.9, royalty: 0.125, transportToMarket: 0.3, hazards: ['storm'],
    risk: [r(1965, 0.95, 0.97, 0, 0.1), r(2025, 0.95, 0.98, 0.01, 0.1)],
    blurb: 'Forties and Brent: deep water, worse weather, impeccable courts.' },
  north_sea_norway: { id: 'north_sea_norway', name: 'North Sea (Norway)', country: 'Norway', continent: 'europe', currency: 'USD',
    lat: 56.5, lon: 3.2, opensYear: 1965, leaseMult: 0.9, royalty: 0.2, transportToMarket: 0.3, hazards: ['storm'],
    risk: [r(1965, 0.97, 0.98, 0, 0.05), r(2025, 0.98, 0.99, 0, 0.05)],
    blurb: 'Ekofisk. The state takes a big share and runs it honestly.' },
  australia: { id: 'australia', name: 'Australia (offshore)', country: 'Australia', continent: 'oceania', currency: 'USD',
    lat: -31.0, lon: 133.0, opensYear: 1965, leaseMult: 0.85, royalty: 0.1, transportToMarket: 0.7, hazards: ['storm'],
    risk: [r(1965, 0.96, 0.97, 0, 0.1), r(2025, 0.96, 0.98, 0, 0.1)],
    blurb: 'Bass Strait’s gales, the Southern Ocean swell and cyclones on the North West Shelf. Fair courts, long distances.' },
  brazil: { id: 'brazil', name: 'Brazil (Santos pre-salt)', country: 'Brazil', continent: 'south_america', currency: 'USD',
    lat: -25.5, lon: -43.0, opensYear: 1997, leaseMult: 0.6, royalty: 0.15, transportToMarket: 0.6, hazards: ['theft', 'storm'],
    risk: [r(1997, 0.7, 0.75, 0.05, 0.6), r(2025, 0.7, 0.75, 0.05, 0.55)],
    blurb: 'Under 7,000 ft of water and a mile of salt.' },
  arctic_russia: { id: 'arctic_russia', name: 'Russian Arctic', country: 'Russia', continent: 'asia', currency: 'USD',
    lat: 72.0, lon: 70.0, opensYear: 1992, leaseMult: 0.4, royalty: 0.2, transportToMarket: 2.5,
    hazards: ['cold', 'expropriation', 'sanctions'],
    risk: [r(1992, 0.4, 0.6, 0.2, 0.8), r(2014, 0.5, 0.7, 0.3, 0.8), r(2022, 0.4, 0.6, 0.7, 0.85), r(2025, 0.4, 0.6, 0.7, 0.85)],
    blurb: 'Yamal ice, sanctions and partners who can change the rules overnight.' },
  // ------------------------------------------------------------------ space (cosmetic risk; claim-jumpers and pirate drones)
  moon: { id: 'moon', name: 'Moon (Shackleton)', country: 'Luna', continent: 'space', currency: 'credits', opensYear: 2060,
    leaseMult: 1, royalty: 0.05, transportToMarket: 5, hazards: ['claim_jumpers'], risk: [r(2060, 0.9, 0.85, 0.02, 0.2)], blurb: 'Ice in permanent shadow.' },
  mars: { id: 'mars', name: 'Mars (Nili Fossae)', country: 'Mars', continent: 'space', currency: 'credits', opensYear: 2065,
    leaseMult: 1, royalty: 0.05, transportToMarket: 20, hazards: ['claim_jumpers', 'pirate_drones'], risk: [r(2065, 0.7, 0.7, 0.05, 0.3)], blurb: 'Clathrates under red dust.' },
  titan: { id: 'titan', name: 'Titan (Kraken Mare)', country: 'Titan', continent: 'space', currency: 'credits', opensYear: 2080,
    leaseMult: 1, royalty: 0.05, transportToMarket: 3, hazards: ['pirate_drones'], risk: [r(2080, 0.6, 0.6, 0.05, 0.3)], blurb: 'Seas of methane and ethane.' },
  europa: { id: 'europa', name: 'Europa', country: 'Europa', continent: 'space', currency: 'credits', opensYear: 2090,
    leaseMult: 1, royalty: 0.05, transportToMarket: 40, hazards: ['radiation', 'pirate_drones'], risk: [r(2090, 0.6, 0.6, 0.05, 0.3)], blurb: 'An ocean under the ice.' },
  // ------------------------------------------------------------------ M2 additions: src/data/m2/regions.js
  ...M2_REGIONS,
  // ------------------------------------------------------------------ M3 additions: src/data/m3/regions.js
  ...M3_REGIONS,
};

// M2: finer 1901-1945 risk history. Keyframes are only inserted after each region's first post-1900
// keyframe, so values for years <= 1900 are unchanged (REGION_RISK_BASE keeps the original lists; tested).
export const REGION_RISK_BASE = Object.fromEntries(Object.entries(REGIONS).map(([id, reg]) => [id, reg.risk.slice()]));
for (const [id, extra] of Object.entries(M2_RISK_KEYFRAMES)) {
  if (REGIONS[id]) REGIONS[id].risk = mergeRiskKeyframes(REGIONS[id].risk, extra, id);
}

// M3: 1946-1999 detail. Inserted after the M2 merge; values for years <= 1946 are unchanged (pinned; tested).
export const REGION_RISK_M2 = Object.fromEntries(Object.entries(REGIONS).map(([id, reg]) => [id, reg.risk.slice()]));
for (const [id, extra] of Object.entries(M3_RISK_KEYFRAMES)) {
  if (REGIONS[id]) REGIONS[id].risk = mergeM3RiskKeyframes(REGIONS[id].risk, extra, id);
}

// M4: 2000-2029 detail. Values for years <= 2000 are unchanged (pinned at 2000.0; tested). The six new M4 regions are
// NON-ENUMERABLE until enableM4Regions() (called by enableM4Fields()), so newGame()/migrate() in M1-M3 create no state
// for them; REGIONS.guyana etc. still resolve by id (M4_SPEC §0).
export const REGION_RISK_M3 = Object.fromEntries(Object.entries(REGIONS).map(([id, reg]) => [id, reg.risk.slice()]));
for (const [id, extra] of Object.entries(m4Obj(M4_RISK_KEYFRAMES))) {
  if (REGIONS[id]) REGIONS[id].risk = mergeM4RiskKeyframes(REGIONS[id].risk, extra, id);
}
for (const reg of Object.values(m4Obj(M4_REGIONS))) Object.defineProperty(REGIONS, reg.id, { value: reg, enumerable: false, writable: true, configurable: true });
let m4RegionsOn = false;
export const m4RegionsEnabled = () => m4RegionsOn;
export function enableM4Regions() {
  if (m4RegionsOn) return false;
  m4RegionsOn = true;
  for (const reg of Object.values(m4Obj(M4_REGIONS))) Object.defineProperty(REGIONS, reg.id, { value: reg, enumerable: true, writable: true, configurable: true });
  return true;
}

export const RISK_KEYS = ['stability', 'security', 'expropriation', 'corruption'];

export function regionRisk(regionId, year) {
  const reg = REGIONS[regionId];
  if (!reg) throw new Error(`unknown region ${regionId}`);
  const k = reg.risk;
  const pickKeys = (o) => Object.fromEntries(RISK_KEYS.map((key) => [key, o[key]]));
  if (year <= k[0].year) return pickKeys(k[0]);
  if (year >= k[k.length - 1].year) return pickKeys(k[k.length - 1]);
  for (let i = 1; i < k.length; i++) {
    if (year <= k[i].year) {
      const a = k[i - 1], b = k[i];
      const t = (year - a.year) / (b.year - a.year);
      return Object.fromEntries(RISK_KEYS.map((key) => [key, a[key] + (b[key] - a[key]) * t]));
    }
  }
  return pickKeys(k[k.length - 1]);
}

// Base daily attack hazard per DESIGN §12.3 (before visibility/guards/calm).
export const BASE_HAZARD = 0.002;
export function baseAttackChance(regionId, year) {
  const { security } = regionRisk(regionId, year);
  return REGIONS[regionId].hazards.length === 0 ? 0 : BASE_HAZARD * (1 - security) ** 2;
}
